import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook for managing timeouts with automatic cleanup
 * Addresses Peter's review feedback on memory leaks from uncleaned timeouts
 */

type TimeoutCallback = () => void;

export const useTimeout = () => {
  const timeoutRefs = useRef<Set<number>>(new Set());

  const setTimeout = useCallback((callback: TimeoutCallback, delay: number): number => {
    const timeoutId = window.setTimeout(() => {
      timeoutRefs.current.delete(timeoutId);
      callback();
    }, delay);
    
    timeoutRefs.current.add(timeoutId);
    return timeoutId;
  }, []);

  const clearTimeout = useCallback((timeoutId: number) => {
    window.clearTimeout(timeoutId);
    timeoutRefs.current.delete(timeoutId);
  }, []);

  const clearAllTimeouts = useCallback(() => {
    timeoutRefs.current.forEach(timeoutId => {
      window.clearTimeout(timeoutId);
    });
    timeoutRefs.current.clear();
  }, []);

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  return {
    setTimeout,
    clearTimeout,
    clearAllTimeouts
  };
};

/**
 * Hook for a single managed timeout
 */
export const useSingleTimeout = () => {
  const timeoutRef = useRef<number | null>(null);

  const setTimeout = useCallback((callback: TimeoutCallback, delay: number) => {
    // Clear existing timeout if any
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      callback();
    }, delay);
    
    return timeoutRef.current;
  }, []);

  const clearTimeout = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout();
    };
  }, [clearTimeout]);

  return {
    setTimeout,
    clearTimeout,
    isActive: timeoutRef.current !== null
  };
};